import { useCallback, useMemo } from 'react';

import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { cx } from 'class-variance-authority';
import { mapWithIndex, filter } from 'fp-ts/lib/Array';
import { match as matchB } from 'fp-ts/lib/boolean';
import { fold, tryCatch } from 'fp-ts/lib/Either';
import { flow, pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromNullable,
  fromPredicate,
  getOrElse,
  map,
  match,
  toUndefined,
} from 'fp-ts/lib/Option';
import { replace } from 'fp-ts/lib/string';
import { z } from 'zod';

import { PressReleaseItem } from '@oresundsbron/api/src/backend/PressRelease';
import { PressRealeaseContactInformation } from '@oresundsbron/api/src/types/contentful/Library/PressReleaseContactInformation';
import { Typography } from '@oresundsbron/bridge-ui';
import { Box } from '@oresundsbron/design-system';
import { CallIcon, EmailIcon, OpenInNewIcon } from '@oresundsbron/icons';
import { formatDate } from '@oresundsbron/utilities';
import { link, NewsType } from '@oresundsbron/validators';

import { Carousel } from '~/components/Carousel';
import { DefaultLink } from '~/components/Links/DefaultLink';

import { CorporateMeny } from '../Corporate/Meny';
import { CorporateSubFooter } from '../Corporatev2';

import FallbackContactInformation from './FallbackContactInformation';
import { PersonType, cropNewsFeedImage } from './utils';
import { useRouter } from 'next/router';

// Max image height and width with 20% added for image animation
const maxImageWidth = 1027;
const maxImageHeight = 809;
const col = cx('col-span-full', 'md:col-start-3', 'md:col-span-8');
type LinkType = z.output<typeof link>;

type SingleNewsPageContentProps = {
  singleNewsInfo: PressReleaseItem;
  locale: string;
  contentfulContactInfo?: PressRealeaseContactInformation;
};

export const SingleNewsPageContent = ({
  singleNewsInfo,
  locale,
  contentfulContactInfo,
}: SingleNewsPageContentProps) => {
  const { t } = useTranslation(['common']);

  const router = useRouter();
  const mainImage = useMemo(
    () =>
      pipe(
        singleNewsInfo,
        fromPredicate((news) => !!news?.mainImage),
        match(
          () => undefined,
          (news) => (
            <Box className="relative col-span-full mb-5 mt-2">
              <Box
                sx={{
                  height: { xs: '350px', sm: '460px', md: '650px' },
                  position: 'relative',
                  gridRow: '1/-1',
                  gridColumn: '1/-1',
                  width: '100%',
                }}
              >
                {news?.mainImage?.url &&
                  (news?.type !== NewsType.news ? ( // Is news from TT/Ritzau
                    <Image
                      src={cropNewsFeedImage(
                        maxImageWidth,
                        maxImageHeight,
                        news?.mainImage?.url
                      )}
                      alt={news?.mainImage?.altText || ''}
                      fill
                      sx={{ objectFit: 'cover', aspectRatio: '16/9' }}
                    />
                  ) : (
                    // Is news from Contentful
                    <Image
                      src={news?.mainImage?.url}
                      alt={news?.mainImage?.altText || ''}
                      fill
                      sx={{ objectFit: 'cover', aspectRatio: '16/9' }}
                    />
                  ))}
              </Box>
              <Typography
                as="span"
                className="text-neutral-700"
                intent="label"
                weight="light"
              >
                {news?.mainImage?.caption}
              </Typography>
            </Box>
          )
        )
      ),
    [singleNewsInfo]
  );

  const imageCarousel = useMemo(
    () =>
      pipe(
        singleNewsInfo,
        fromNullable,
        chain((data) => pipe(data?.images, fromPredicate(Array.isArray))),
        chain(
          flow(
            fromPredicate((im) => im.length > 1),
            map(
              mapWithIndex((i, img) => (
                <Box key={`image-wrapper-${i}`}>
                  <Image
                    key={i}
                    alt={img.altText || ''}
                    src={img.url}
                    priority={i === 0}
                    sizes="(max-width: 768px) 100vw,
                (max-width: 1200px) 50vw,
                33vw"
                    fill
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                  <Box className="absolute inset-x-0 bottom-0 w-full bg-primary-400 bg-opacity-75 p-2">
                    <Typography color="white">{img.title}</Typography>
                  </Box>
                </Box>
              ))
            ),
            map((imgs) => (
              <Box className={cx(col, 'relative')}>
                <Carousel>{imgs}</Carousel>
              </Box>
            ))
          )
        ),
        getOrElse(() => <></>)
      ),
    [singleNewsInfo]
  );

  const refineContent = useMemo(
    () =>
      pipe(
        singleNewsInfo?.body,
        fromNullable,
        map((cont) =>
          pipe(
            tryCatch(
              () =>
                pipe(
                  cont as string,
                  replace('\n', ''),
                  replace(/<p>/g, "<p class='pb-5 break-words'>"),
                  replace(
                    /<h3>/g,
                    "<h2 class='leading-normal text-inherit text-[1.25rem] font-medium first:pt-0 last:pb-0 pt-2'>"
                  ),
                  replace(/<\/h3>/g, '</h2>'),
                  replace(/<strong>/g, '<span class="font-semibold">'),
                  replace(/<\/strong>/g, '</span>'),
                  replace(/<li>/g, '<li class="pb-3" >'),
                  replace(/<table/g, "<table class='w-full'"),
                  replace(
                    /<tr/g,
                    "<tr class='border-b border-b-black last:mb-2 '"
                  ),
                  replace(
                    /<td/g,
                    "<td class='text-left p-4 last:pr-0 md:pt-6 md:pb-0 md:pl-0 md:pr-2 align-top md:align-middle'"
                  ),
                  replace(/<a/g, "<a class='text-primary-700 underline'"),
                  replace(/http(?![s])/g, 'https')
                ),
              (error) => error as Error
            ),
            fold(
              () => '',
              (modified) => modified
            )
          )
        ),
        getOrElse(() => '')
      ),
    [singleNewsInfo]
  );

  const formatPhoneNumber = (phone?: string) =>
    pipe(
      phone,
      fromNullable,
      map((p) => p.replace(/\D/g, '')),
      map((p) => p.replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3 $4')),
      getOrElse(() => '')
    );

  const contactInfoView = useMemo(() => {
    const contactInfo = pipe(
      singleNewsInfo,
      fromNullable,
      chain(
        fromPredicate(
          ({ contacts }) => !!contacts && contacts.persons.length > 0
        )
      ),
      map(({ contacts }) => (
        <Box className="mb-10">
          <Typography intent="title" className="mb-5">
            {t('pressReleases.contactTitle')}
          </Typography>
          <Box className="xs:grid-cols-1 col-span-full grid md:grid-cols-2">
            {pipe(
              contacts?.persons as PersonType[],
              mapWithIndex((i, person) => (
                <Box
                  key={`press-contact-${i}`}
                  className="flex items-center pb-3 md:pb-0"
                >
                  {pipe(
                    person?.image,
                    fromPredicate((i) => !!i && !!i.url),
                    map((image) => (
                      <Box className="min-w-40">
                        <Box
                          className={`relative h-40 w-40 overflow-hidden rounded-full`}
                        >
                          <Image
                            className="transition ease-in-out group-hover:scale-110"
                            alt={image?.caption ?? ''}
                            sizes="(max-width: 768px) 50vw,20vw"
                            quality={50}
                            fill
                            style={{
                              objectFit: 'cover',
                              aspectRatio: '16/10',
                            }}
                            src={image?.url ?? ''}
                          />
                        </Box>
                      </Box>
                    )),
                    toUndefined
                  )}
                  <Box className="flex flex-col pl-3">
                    <Typography
                      as="span"
                      className="text-[1.2rem] text-primary-900"
                      weight="medium"
                    >
                      {person?.name}
                    </Typography>
                    <Typography
                      as="span"
                      className="mb-2 text-[0.9rem] text-neutral-800"
                      weight="light"
                    >
                      {person?.title}
                    </Typography>
                    {!!person?.email && (
                      <Box className="mb-1 flex items-center">
                        <EmailIcon className="fill-blue-900" />
                        <DefaultLink
                          href={`mailto:${person.email}`}
                          target="_self"
                          underline={false}
                        >
                          <Typography
                            as="span"
                            className="pl-2 text-[0.92rem] text-primary-700 hover:underline sm:text-[1rem]"
                            weight="light"
                          >
                            {person.email}
                          </Typography>
                        </DefaultLink>
                      </Box>
                    )}
                    {!!person?.phone && (
                      <Box className="mb-1 flex items-center">
                        <CallIcon className="fill-blue-900" />
                        <DefaultLink
                          href={`tel:${person.phone}`}
                          target="_self"
                          underline={false}
                        >
                          <Typography
                            as="span"
                            className="pl-2 text-[0.92rem] text-primary-700 hover:underline sm:text-[1rem]"
                            weight="light"
                          >
                            {formatPhoneNumber(person.phone)}
                          </Typography>
                        </DefaultLink>
                      </Box>
                    )}
                    {pipe(
                      person?.contactLinks,
                      fromPredicate((c) => !!c?.linkedIn || !!c?.twitter),
                      map((c) => (
                        <Box className="mt-2 flex">
                          {!!c?.linkedIn && (
                            <DefaultLink
                              target="_blank"
                              intent={'label'}
                              underline={false}
                              className="pr-2 font-normal text-primary-700"
                              href={c?.linkedIn}
                            >
                              {t('pressReleases.contactInfo.linkedin')}
                            </DefaultLink>
                          )}
                          {!!c?.twitter && (
                            <>
                              <DefaultLink
                                target="_blank"
                                intent={'label'}
                                className="font-normal text-primary-700"
                                underline={false}
                                href={`https://twitter.com/${c?.twitter}`}
                              >
                                {t('pressReleases.contactInfo.twitter')}
                              </DefaultLink>
                            </>
                          )}
                        </Box>
                      )),
                      toUndefined
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )),
      toUndefined
    );
    return contactInfo ? (
      contactInfo
    ) : (
      <FallbackContactInformation
        heading={contentfulContactInfo?.title}
        description={contentfulContactInfo?.preamble}
        contacts={contentfulContactInfo?.contactInformation ?? []}
      />
    );
  }, [singleNewsInfo, contentfulContactInfo, t]);

  const linkAndDocView = useCallback(
    (isDoc?: true) =>
      pipe(
        singleNewsInfo,
        fromPredicate((n) =>
          pipe(
            !!isDoc,
            matchB(
              () => !!n?.links && n.links.length > 0,
              () => !!n?.documents && n.documents.length > 0
            )
          )
        ),
        map((n) => (
          <Box className="mb-10">
            <Typography intent="title" className="mt-5">
              {!!isDoc
                ? t('pressReleases.documentsTitle')
                : t('pressReleases.linksTitle')}
            </Typography>
            <Box className="col-span-full">
              {pipe(
                (!!isDoc ? n?.documents : n?.links) ?? [],
                filter((l) => !!l?.url),
                mapWithIndex((i, ld) => (
                  <DefaultLink
                    key={`linkdoc-${i}`}
                    className="mb-1 flex items-center"
                    target="_blank"
                    href={ld?.url || ''}
                  >
                    <Typography className="pr-2 text-primary-700" as="span">
                      {pipe(
                        !!isDoc,
                        matchB(
                          () =>
                            (ld as LinkType)?.description ??
                            t('pressReleases.linksDefault'),
                          () => ld?.title ?? t('pressReleases.documentsDefault')
                        )
                      )}
                    </Typography>
                    <OpenInNewIcon />
                  </DefaultLink>
                ))
              )}
            </Box>
          </Box>
        )),
        toUndefined
      ),
    [singleNewsInfo, t]
  );

  return (
    <Box as="main">
      <div className="mx-auto grid max-w-7xl grid-cols-12 px-4">
        <CorporateMeny className="col-span-full md:col-span-3 md:mt-8" />
        <Box className="col-span-full md:col-span-9">
          <Box className="mt-8 grid gap-8 sm:gap-16">
            <div>
              <span className="max-w-1/2 truncate pr-2 font-semibold uppercase tracking-wider text-blue-600">
                {(singleNewsInfo.id as number) > 0
                  ? t('newsroom.labelFromTTRitzau')
                  : t('newsroom.labelFromContentful')}
              </span>

              {singleNewsInfo?.published && (
                <Typography
                  className="text-neutral-700"
                  as="span"
                  size="lg"
                  weight="normal"
                >
                  {formatDate(
                    singleNewsInfo.published,
                    locale || router.locale
                  )}
                </Typography>
              )}
            </div>
          </Box>
          <Typography as="h1" className="mb-5 mt-2" intent="headline">
            {singleNewsInfo?.title}
          </Typography>
          <Typography as="p" className="mb-5 font-normal" intent="title">
            {singleNewsInfo?.leadtext}
          </Typography>
          {mainImage}
          <Box dangerouslySetInnerHTML={{ __html: refineContent ?? '' }} />
          <Box className="relative col-span-full mb-10 mt-10">
            {imageCarousel}
          </Box>
          {contactInfoView}
          {linkAndDocView()}
          {linkAndDocView(true)}
        </Box>
      </div>

      <CorporateSubFooter />
    </Box>
  );
};
